import { ControlOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { RoleTabId } from '../../utils/roleTabs'
import { ROLE_TAB_COUNT_KEYS } from '../../utils/roleTabs'
import type { SortKey } from '../../utils/sort'
import TeamListSortDropdown from './TeamListSortDropdown'

type TeamListToolbarProps = {
  searchValue: string
  onSearchChange: (value: string) => void
  searchVisible?: boolean
  onFiltersClick?: () => void
  // Custom filter control (popover / side-panel trigger) rendered in place of
  // the default Filters button. Each screen injects its own surface (CM-3581).
  filtersControl?: React.ReactNode
  sortKey: SortKey
  onSortChange: (value: SortKey) => void
  // Role-count caption (CM-4559): the tab picks the noun, the number is how
  // many rows the current tab/search/filters actually yield.
  activeTab: RoleTabId
  count: number
  testId?: string
}

const StyledRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 16px;
  padding: 8px 16px;
  /* 56px is the whole strip in the design, padding included. */
  box-sizing: border-box;
  min-height: 56px;
  background-color: ${({ theme }) => theme.color.neutral.color10};
`

// Desktop: the count shares the controls row, taking the free space on the
// left. Mobile: it drops to its own line above the search box, one step down
// in type size (Figma 9229-61309).
const StyledCount = styled.p`
  flex: 1 0 0;
  min-width: 256px;
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color4};

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-basis: 100%;
    min-width: 0;
    font-size: 14px;
    line-height: 16px;
  }
`

const StyledSearchInput = styled(Input)`
  width: 256px;
  height: 40px;
  border-radius: 6px !important;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
`

const StyledRightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: 0;
    width: 100%;
  }
`

const SEARCH_DEBOUNCE_MS = 150

const TeamListToolbar: React.FC<TeamListToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchVisible = true,
  onFiltersClick,
  filtersControl,
  sortKey,
  onSortChange,
  activeTab,
  count,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const [draft, setDraft] = useState(searchValue)

  useEffect(() => setDraft(searchValue), [searchValue])

  useEffect(() => {
    if (draft === searchValue) {
      return
    }
    const handle = setTimeout(() => onSearchChange(draft), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [draft, searchValue, onSearchChange])

  return (
    <StyledRow data-testid={testId}>
      <StyledCount data-testid={testId ? `${testId}-count` : undefined}>
        {t(ROLE_TAB_COUNT_KEYS[activeTab], { count })}
      </StyledCount>
      {searchVisible && (
        <StyledSearchInput
          id="team-list-search"
          aria-label={t(reportsConstants.TEAM_LIST_SEARCH_ARIA)}
          placeholder={t(reportsConstants.TEAM_LIST_SEARCH_PLACEHOLDER)}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          suffix={<SearchOutlined />}
          data-testid={testId ? `${testId}-search` : undefined}
        />
      )}
      <StyledRightControls>
        {filtersControl ?? (
          <Button
            variant="default"
            icon={<ControlOutlined />}
            onClick={onFiltersClick}
            data-testid={testId ? `${testId}-filters` : undefined}
          >
            {t(reportsConstants.TEAM_LIST_FILTERS)}
          </Button>
        )}
        <TeamListSortDropdown
          value={sortKey}
          onChange={onSortChange}
          testId={testId ? `${testId}-sort` : undefined}
        />
      </StyledRightControls>
    </StyledRow>
  )
}

export default TeamListToolbar
